import React from 'react';
import parseQS from 'helpers/parseQS';
import { useTranslate } from 'hooks/useTranslate';
import { useLocation, Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from 'assets/icons/search_icon.svg?react';
import FilterIcon from 'assets/icons/iconoir_sort-up.svg?react';
import useDataListFactory from 'services/dataList';
import { IS_SNAP } from 'variables/main';

const Helmet = React.lazy(() => import('components/Helmet'));
const Drawer = React.lazy(() => import('components/Drawer'));
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const Cards = React.lazy(() => import('components/Card'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));
const LinearProgress = React.lazy(() => import('components/LinearProgress'));
const RenderPageSchema = React.lazy(() => import('components/RenderPageSchema'));

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.backgroundNeutral.main
}));

const HomeProfessionsWrapper = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    paddingBottom: 30
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 25
  }
}));

const DescriptionBold = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  fontSize: 21,
  lineHeight: '27px',
  fontWeight: 600,
  marginBottom: 20,
  [theme.breakpoints.down('sm')]: {
    fontSize: 21,
    lineHeight: '27px'
  }
}));

const DescriptionNormal = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  marginBottom: 10,
  marginTop: 10
}));

const DescriptionBottom = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  marginBottom: 10,
  marginTop: 60
}));

const ActionsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 40,
  [theme.breakpoints.down('md')]: {
    marginBottom: 20
  }
}));

const TextFieldStyled = styled(TextField)(() => ({
  borderRadius: 8
}));

const FilterIconStyled = styled(FilterIcon)(() => ({
  marginRight: 10,
  width: 43,
  height: 43,
  cursor: 'pointer'
}));

const ButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '0 auto'
}));

const ClearIconStyled = styled(ClearIcon)(({ theme }) => ({
  cursor: 'pointer',
  marginRight: 10,
  color: theme.palette.textPrimary.main
}));

const ListImageComponent = styled('img')(() => ({
  width: '100%',
  maxWidth: 400,
  marginBottom: 40
}));

const FlexDescription = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 50,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 15
  }
}));

const PageLayout = ({
  endPoint,
  translate,
  innerPage,
  breadcrumbs: breadcrumbsProp,
  Filters,
  CardTemplate,
  title,
  descriptionBold,
  descriptionNormal,
  descriptionBottom,
  meta,
  // Opt-in: only routes that exist on BOTH brands should emit hreflang.
  includeHreflang = false,
  loading: loadingProp,
  useFavorites,
  ListImage,
  additionalComponent,
  getRedirectUrl,
  externalLink,
  borderedCard,
  ChosenFilters
}) => {
  const location = useLocation();
  const [initFilters, setInitFilters] = React.useState();
  const [qsPage] = React.useState(() => {
    if (!IS_SNAP) return null;
    const pageNumber = (location?.pathname || '').split('/').pop() || null;
    if (pageNumber && !Number.isNaN(Number(pageNumber))) {
      return Number(pageNumber);
    }
    return null;
  });

  const { actions, data, loading, page, perPage, count, filters, sort } = useDataList(endPoint, {
    page: qsPage || 1
  });

  const [open, setOpen] = React.useState(false);

  const t = useTranslate(translate);

  const handleOpenDrawer = React.useCallback(() => setOpen(true), []);

  const handleCLoseDrawer = React.useCallback(() => setOpen(false), []);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeSearch = React.useCallback(
    (e) => {
      actions.onSearchChange(e.target.value);
    },
    [actions]
  );

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleChangeSearch(e);
      }
    },
    [handleChangeSearch]
  );

  const handleClearSearch = React.useCallback(() => {
    actions.clearData(true);
  }, [actions]);

  const cardTemplate = React.useCallback(
    (item) => (CardTemplate ? <CardTemplate t={t} item={item} /> : null),
    [CardTemplate, t]
  );

  const limitReached = React.useMemo(() => {
    if (qsPage && count <= qsPage * perPage) {
      return true;
    }
    return count <= page * perPage;
  }, [count, page, perPage, qsPage]);

  const breadcrumbs = React.useMemo(() => breadcrumbsProp, [breadcrumbsProp]);

  React.useEffect(() => {
    if (initFilters) return;
    setInitFilters(true);
    handleChangeSearch({
      target: { value: parseQS(location.search).search || '' }
    });
  }, [handleChangeSearch, initFilters, location.search]);

  return (
    <>
      <Helmet meta={meta} includeHreflang={includeHreflang} />
      <MainLayout>
        <Header />
        <Background>
          <HomeProfessionsWrapper>
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            {loadingProp ? null : (
              <>
                <Title variant="h1">{title || t('Title')}</Title>

                {ListImage ? (
                  <FlexDescription>
                    <div>
                      {descriptionBold ? (
                        <DescriptionBold variant="h2">{descriptionBold}</DescriptionBold>
                      ) : null}
                      {descriptionNormal ? (
                        <DescriptionNormal variant="body1">{descriptionNormal}</DescriptionNormal>
                      ) : null}
                    </div>
                    <ListImageComponent src={ListImage} alt="" />
                  </FlexDescription>
                ) : (
                  <>
                    {descriptionBold ? (
                      <DescriptionBold variant="h2">{descriptionBold}</DescriptionBold>
                    ) : null}
                    {descriptionNormal ? (
                      <DescriptionNormal variant="body1">{descriptionNormal}</DescriptionNormal>
                    ) : null}
                  </>
                )}
              </>
            )}

            <ActionsWrapper>
              <FilterIconStyled onClick={handleOpenDrawer} />
              <TextFieldStyled
                value={filters?.name || ''}
                onChange={handleChangeSearch}
                onKeyDown={onKeyDown}
                placeholder={t('Search')}
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        {filters?.name ? <ClearIconStyled onClick={handleClearSearch} /> : null}
                        <SearchIcon />
                      </>
                    )
                  }
                }}
              />
            </ActionsWrapper>

            {ChosenFilters ? <ChosenFilters filters={filters} actions={actions} /> : null}

            <Cards
              list={data}
              template={cardTemplate}
              endPoint={innerPage}
              useFavorites={useFavorites}
              getRedirectUrl={getRedirectUrl}
              externalLink={externalLink}
              borderedCard={borderedCard}
            />

            {data && data.length === 0 ? <Typography>{t('NoData')}</Typography> : null}

            {loading || loadingProp || !data ? <LinearProgress /> : null}

            {limitReached ? null : (
              <>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  onClick={handleChangePage}
                  aria-label={t('LoadMore')}>
                  {t('LoadMore')}
                </ButtonStyled>
                <Link to={`/${innerPage}/page/${page + 1}`} style={{ display: 'none' }}>
                  {t('LoadMore')}
                </Link>
              </>
            )}
            {descriptionBottom ? (
              <DescriptionBottom>
                <RenderPageSchema
                  hideTitle={true}
                  schema={{
                    layout: [descriptionBottom]
                  }}
                />
              </DescriptionBottom>
            ) : null}
          </HomeProfessionsWrapper>
        </Background>

        {additionalComponent || null}

        <Footer />
        <Drawer anchor={'left'} open={open} onClose={handleCLoseDrawer}>
          <Filters actions={actions} filters={filters} sort={sort} />
        </Drawer>
      </MainLayout>
    </>
  );
};

export default PageLayout;
